import { useId, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Field, fieldDescribedBy } from '@src/components/Field/Field';
import {
  isDuplicateName,
  isValidName,
  parseNames,
} from '@src/components/NamesInput/parseNames';
import { Tag } from '@src/components/Tag/Tag';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { typeClassName } from '@src/components/Typography/Typography';
import './NamesInput.css';

/** Why a token was refused. The screen owns the wording. */
export type NamesProblem = 'invalid' | 'duplicate' | 'tooMany';

export interface NamesInputProps {
  /** Controlled: the names already committed to chips. */
  names: string[];
  onNamesChange: (names: string[]) => void;
  label?: string;
  /** Shown opposite the label. Usually the message for the last problem. */
  error?: string;
  hint?: string;
  placeholder?: string;
  /** Sits opposite the hint — the participant count, typically. */
  hintEnd?: React.ReactNode;
  maxNames?: number;
  /** Existing participants that must not be added again. */
  reservedNames?: string[];
  /** Accessible name for a chip's remove mark. */
  removeLabel?: (name: string) => string;
  /** Fires with null as soon as the field is usable again. */
  onProblem?: (problem: NamesProblem | null) => void;
  disabled?: boolean;
  /** Height of the typing area, in lines. */
  rows?: number;
  /** When false, committed names are not shown as chips — only in external UI. */
  showTags?: boolean;
  /** Submit the unfinished name on blur. Disable for collapsible draft inputs. */
  commitOnBlur?: boolean;
  /** Flush inside a parent shell — no field border (participant table compose). */
  embedded?: boolean;
  /** Parent id. Chips, the control and the messages hang off it. */
  testId?: string;
}

/**
 * A list of names built by typing: every comma, newline or Enter turns what was
 * typed into a chip, and a pasted list becomes chips all at once. Backspace on
 * an empty line removes the last one.
 *
 * The typing area is a textarea rather than an input so that a pasted
 * multi-line list is not silently flattened before it can be split.
 */
export function NamesInput({
  names,
  onNamesChange,
  label,
  error,
  hint,
  placeholder,
  hintEnd,
  maxNames = 100,
  reservedNames = [],
  removeLabel = (name) => `Remove ${name}`,
  onProblem,
  disabled = false,
  rows = 6,
  showTags = true,
  commitOnBlur = true,
  embedded = false,
  testId,
}: NamesInputProps) {
  const controlId = useId();
  const [draft, setDraft] = useState('');
  const controlRef = useRef<HTMLTextAreaElement>(null);
  const singleLine = rows === 1;

  /**
   * Commit as many tokens as pass the rules. The first one that fails stays in
   * the field as the draft, so the name being complained about is the one the
   * user can still edit.
   */
  function commit(tokens: string[], leftover = '') {
    if (tokens.length === 0) {
      setDraft(leftover);
      return;
    }
    const next = [...names];
    for (const token of tokens) {
      const problem: NamesProblem | null = !isValidName(token)
        ? 'invalid'
        : isDuplicateName(token, [...reservedNames, ...next])
          ? 'duplicate'
          : next.length >= maxNames
            ? 'tooMany'
            : null;
      if (problem) {
        setDraft(token);
        onProblem?.(problem);
        onNamesChange(next);
        return;
      }
      next.push(token.trim());
    }
    setDraft(leftover);
    onProblem?.(null);
    onNamesChange(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit(parseNames(event.currentTarget.value));
      return;
    }
    if (event.key === 'Backspace' && !draft && names.length > 0) {
      onNamesChange(names.slice(0, -1));
      onProblem?.(null);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const text = event.clipboardData.getData('text');
    if (!text || !/[,;\n\r]/.test(text)) {
      return;
    }
    event.preventDefault();
    commit(parseNames(`${event.currentTarget.value}${text}`));
  }

  return (
    <Field
      controlId={controlId}
      label={label}
      error={error}
      hint={hint}
      hintEnd={hintEnd}
      disabled={disabled}
      multiline={!singleLine}
      borderless={embedded}
      testId={testId}
    >
      {/* Clicking the padding around the chips should put the caret in the
          field, the way it does in a plain textarea. */}
      <div
        className={singleLine ? 'names-input names-input--single-line' : 'names-input'}
        role="presentation"
        onClick={() => controlRef.current?.focus()}
      >
        {showTags && names.length > 0 && (
          <div className="names-input__tags" {...testIdAttr(testIdFor(testId, 'tags'))}>
            {names.map((name) => (
              <Tag
                key={name}
                dismissLabel={removeLabel(name)}
                disabled={disabled}
                testId={testIdFor(testId, 'tag')}
                onDismiss={() => {
                  onNamesChange(names.filter((item) => item !== name));
                  onProblem?.(null);
                }}
              >
                {name}
              </Tag>
            ))}
          </div>
        )}
        <textarea
          ref={controlRef}
          className={`names-input__control ${typeClassName('label/input')}`}
          id={controlId}
          value={draft}
          rows={rows}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={fieldDescribedBy(controlId, { error, hint })}
          onChange={(event) => {
            const value = event.target.value;
            if (error) {
              onProblem?.(null);
            }
            if (/[,;\n\r]/.test(value)) {
              commit(parseNames(value));
              return;
            }
            setDraft(value);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={(event) => {
            if (commitOnBlur && event.currentTarget.value.trim()) {
              commit(parseNames(event.currentTarget.value));
            }
          }}
          {...testIdAttr(testIdFor(testId, 'control'))}
        />
      </div>
    </Field>
  );
}
